'use client';

import React  from 'react';
import { useGetBroadcastDetailsQuery } from '@/client/api/whatsapp/broadcast-details.query';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Clock,  CheckCircle, XCircle, Users } from 'lucide-react';
import { Recipient } from '@/client/types/whatsapp/broadcast-campaigns';

interface BroadcastDetailsModalProps {
  broadcastId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onClose: () => void;
}


const BroadcastDetailsModal: React.FC<BroadcastDetailsModalProps> = ({
  broadcastId,
  open,
  onOpenChange,
  onClose,
}) => {

  const { data, isLoading, error } = useGetBroadcastDetailsQuery(broadcastId, {
    enabled: open, // Only fetch when modal is open
  });

  const formatError = (error: string): string => {
    try {
      const parsed = JSON.parse(error);
      if (parsed.error?.message) {
        return parsed.error.message.replace(/^\(#\d+\)\s*/, '');
      }
      if (parsed.error?.details) {
        return parsed.error.details;
      }
      return 'Delivery failed';
    } catch {
      return error.length > 100 ? error + '...' : error;
    }
  };


  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
        return 'bg-green-100 text-green-800 border-green-200 hover:bg-green-50';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-50';
      case 'pending':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200 hover:bg-yellow-50';
      case 'canceled':
        return 'bg-red-100 text-red-800 border-red-200 hover:bg-red-50';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200 hover:bg-gray-50';
    }
  };

  const getRecipientStatusColor = (status: Recipient['status']) => {
    switch (status) {
      case 'sent':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'failed':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'skipped':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatTimestamp = (timestamp: number) => {
    return new Date(timestamp * 1000).toLocaleString();
  };

  const SkeletonLoader = () => (
    <div className="space-y-6">
      {/* Header Skeleton */}
      <div className="space-y-3">
        <Skeleton className="h-8 w-3/4" />
        <Skeleton className="h-4 w-1/2" />
      </div>

      {/* Stats Skeleton */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[...Array(4)].map((_, i) => (
          <Skeleton key={i} className="h-20 w-full" />
        ))}
      </div>

      {/* Details Skeleton */}
      <div className="space-y-4">
        <Skeleton className="h-6 w-1/3" />
        <div className="grid grid-cols-2 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="space-y-2">
              <Skeleton className="h-4 w-20" />
              <Skeleton className="h-5 w-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Recipients Skeleton */}
      <div className="space-y-3">
        <Skeleton className="h-6 w-1/4" />
        <div className="space-y-2">
          {[...Array(5)].map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] p-0">
        <DialogHeader className="p-6 border-b">
          <DialogTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Broadcast Details
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 max-h-[calc(90vh-100px)] overflow-hidden">
          <ScrollArea className="h-full pr-4">
            {isLoading ? (
              <SkeletonLoader />
            ) : error ? (
              <div className="text-center py-12 text-red-600">
                <XCircle className="h-12 w-12 mx-auto mb-4" />
                <h3 className="text-lg font-medium mb-2">Failed to load broadcast details</h3>
                <p className="text-sm opacity-75">Please try again later</p>
              </div>
            ) : data?.status === 200 && data.data ? (
              <div className="space-y-6">
                {/* Campaign Header */}
                <Card>
                  <CardHeader className="pb-4">
                    <CardTitle className="flex items-center gap-2 text-xl">
                      {data.data.name || 'Untitled Broadcast'}
                    </CardTitle>
                    <div>
                      <Badge className={getStatusColor(data.data.status)} variant="outline">
                        {data.data.status.toUpperCase()}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {data.data.description && (
                      <p className="text-sm text-muted-foreground mb-4">
                        {data.data.description}
                      </p>
                    )}
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-muted-foreground">Template</span>
                        <p className="font-medium">{data.data.template_name}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">Language</span>
                        <p className="font-medium">{data.data.language_code}</p>
                      </div>
                      <div>
                        <span className="text-muted-foreground">From</span>
                        <p className="font-medium">{data.data.from_number}</p>
                      </div>
                      <div className="md:col-span-2 lg:col-span-1">
                        <span className="text-muted-foreground">Campaign ID</span>
                        <p className="font-mono text-xs break-all">
                          {data.data.campaign_id}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Stats Cards */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <span className="text-sm font-medium">Total</span>
                      <Users className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{data.data.total_recipients}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <span className="text-sm font-medium">Sent</span>
                      <CheckCircle className="h-4 w-4 text-green-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-green-600">{data.data.sent}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <span className="text-sm font-medium">Failed</span>
                      <XCircle className="h-4 w-4 text-red-500" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold text-red-600">{data.data.failed}</div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                      <span className="text-sm font-medium">Skipped</span>
                      <Clock className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">{data.data.skipped}</div>
                    </CardContent>
                  </Card>
                </div>

                {/* Timeline */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">Timeline</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div className="flex items-center gap-3 text-sm">
                      <div className="w-2 h-2 bg-green-500 rounded-full" />
                      <span>Created: {formatTimestamp(data.data.created_at)}</span>
                    </div>
                    {data.data.started_at && (
                      <div className="flex items-center gap-3 text-sm pl-5">
                        <div className="w-2 h-2 bg-blue-500 rounded-full" />
                        <span>Started: {formatTimestamp(data.data.started_at)}</span>
                      </div>
                    )}
                    {data.data.completed_at && (
                      <div className="flex items-center gap-3 text-sm pl-5">
                        <div className="w-2 h-2 bg-green-500 rounded-full" />
                        <span>Completed: {formatTimestamp(data.data.completed_at)}</span>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Recipients Table */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center justify-between">
                      Recipients ({data.data.total_recipients})
                      <Badge variant="outline" className="text-xs">
                        Rate Limit: {data.data.rate_limit}%
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {data.data.recipients.map((recipient: Recipient, index: number) => (
                        <div key={index} className="flex flex-col p-3 border rounded-lg">
                          <div key={index} className="flex gap-3 flex-col md:flex-row md:items-center justify-between p-3 borderl rounded-lg">
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-10 h-10 bg-gradient-to-r from-blue-500 to-blue-600 rounded-full flex items-center justify-center">
                                <span className="text-white font-medium text-sm">
                                  {recipient.name.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="font-medium truncate">{recipient.name}</p>
                                <p className="text-sm text-muted-foreground truncate">{recipient.phone}</p>
                              </div>
                            </div>
                            <div className='flex gap-2 flex-col justify-end align-sub'>
                              <div>
                                <Badge className={getRecipientStatusColor(recipient.status)} variant="outline">
                                  {recipient.status.toUpperCase()}
                                </Badge>
                              </div>
                            </div>
                          </div>
                          {recipient.error && (
                            <div className="ml-2 text-[10px]  text-red-600 " title={recipient.error}>
                              {formatError(recipient.error)}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            ) : (
              <div className="text-center py-12 text-gray-500">
                No broadcast data available
              </div>
            )}
          </ScrollArea>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default BroadcastDetailsModal;